import { useEffect, useState } from "react";
import { groupMinistries, groupTitle } from "@/lib/ministries";
import { VolunteersDialog } from "@/components/VolunteersDialog";
import { fmtDate } from "@/lib/dates";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { HeartHandshake, Plus, Pencil, Trash2, Users, Clock, UserCircle, ClipboardList, ChevronDown } from "lucide-react";

const COLLAPSE_KEY = "whf-ministry-collapsed";

const TINTS = ["#2D5A27", "#D97706", "#0284C7", "#9A3412", "#84A98C", "#B45309"];
const EMPTY = { name: "", leader: "", description: "", schedule: "" };

function MinistryCard({ m, tint, onEdit, onRemove, onRoster, onVolunteers }) {
  return (
    <Card className="p-6 bg-white border border-[#E5E0D8] shadow-sm flex flex-col" data-testid={`ministry-card-${m.id}`}>
      <div className="flex items-start justify-between">
        <div className="h-12 w-12 rounded-xl flex items-center justify-center text-white" style={{ backgroundColor: tint }}>
          <HeartHandshake className="h-6 w-6" />
        </div>
        <div className="flex gap-1">
          <Button size="icon" variant="ghost" onClick={() => onEdit(m)} data-testid={`edit-ministry-${m.id}`} className="h-8 w-8 text-[#526050]">
            <Pencil className="h-4 w-4" />
          </Button>
          <Button size="icon" variant="ghost" onClick={() => onRemove(m.id)} data-testid={`delete-ministry-${m.id}`} className="h-8 w-8 text-destructive">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <button type="button" onClick={() => onVolunteers(m)} data-testid={`ministry-title-${m.id}`}
        className="mt-4 text-left font-serif text-xl font-semibold text-[#1C241B] hover:text-[#2D5A27] hover:underline underline-offset-4">{m.name}</button>
      <p className="mt-1 text-sm text-[#526050] flex-1">{m.description || "No description"}</p>
      <div className="mt-4 space-y-2 text-sm">
        <div className="flex items-center gap-2 text-[#526050]"><UserCircle className="h-4 w-4 text-[#7A8A78]" />{m.leader || "No leader assigned"}</div>
        <div className="flex items-center gap-2 text-[#526050]"><Clock className="h-4 w-4 text-[#7A8A78]" />{m.schedule || "No schedule"}</div>
        <button type="button" onClick={() => onVolunteers(m)} data-testid={`ministry-volunteers-${m.id}`}
          className="flex items-center gap-2 text-[#2D5A27] font-medium hover:underline underline-offset-4"><Users className="h-4 w-4" />{m.member_count} volunteers · view</button>
      </div>
      <Button variant="outline" onClick={() => onRoster(m)} data-testid={`roster-ministry-${m.id}`}
        className="mt-4 w-full border-[#2D5A27] text-[#2D5A27] hover:bg-[#EAF2E8]">
        <ClipboardList className="h-4 w-4 mr-2" /> Manage Roster
      </Button>
    </Card>
  );
}

function GroupHeader({ group, tint, onEdit, onRemove, onRoster, onVolunteers, collapsed, onToggle }) {
  const m = group.main;
  const title = groupTitle(group);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-l-4 pl-2 py-1" style={{ borderColor: tint }}
      data-testid={`ministry-group-${group.key}`}>
      <button type="button" onClick={onToggle} aria-expanded={!collapsed}
        data-testid={`ministry-group-toggle-${group.key}`}
        className="flex items-start gap-2 text-left rounded-lg px-2 py-1 hover:bg-[#F1EFEA] transition-colors">
        <ChevronDown className={`mt-2 h-5 w-5 shrink-0 text-[#7A8A78] transition-transform duration-200 ${collapsed ? "-rotate-90" : ""}`} />
        <div>
        <h2 className="font-serif text-2xl font-bold text-[#1C241B]" data-testid={`ministry-group-title-${group.key}`}>{title}</h2>
        <p className="text-xs text-[#7A8A78]">
          {group.subs.length} sub-ministr{group.subs.length === 1 ? "y" : "ies"}
          {m && <> · {m.leader || "No leader assigned"} · {m.member_count} volunteers</>}
        </p>
        </div>
      </button>
      {m && (
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={() => onVolunteers(m)} data-testid={`ministry-volunteers-${m.id}`}
            className="text-[#2D5A27] hover:bg-[#EAF2E8]">
            <Users className="h-4 w-4 mr-1" /> {m.member_count} volunteers
          </Button>
          <Button size="sm" variant="outline" onClick={() => onRoster(m)} data-testid={`roster-ministry-${m.id}`}
            className="border-[#2D5A27] text-[#2D5A27] hover:bg-[#EAF2E8]">
            <ClipboardList className="h-4 w-4 mr-1" /> Roster
          </Button>
          <Button size="icon" variant="ghost" onClick={() => onEdit(m)} data-testid={`edit-ministry-${m.id}`} className="h-8 w-8 text-[#526050]">
            <Pencil className="h-4 w-4" />
          </Button>
          <Button size="icon" variant="ghost" onClick={() => onRemove(m.id)} data-testid={`delete-ministry-${m.id}`} className="h-8 w-8 text-destructive">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

export default function Ministries() {
  const [ministries, setMinistries] = useState([]);
  const [collapsed, setCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem(COLLAPSE_KEY)) || []; } catch { return []; }
  });
  const saveCollapsed = (next) => { setCollapsed(next); localStorage.setItem(COLLAPSE_KEY, JSON.stringify(next)); };
  const toggleGroup = (key) => saveCollapsed(collapsed.includes(key) ? collapsed.filter((k) => k !== key) : [...collapsed, key]);
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [rosterFor, setRosterFor] = useState(null);
  const [volunteersFor, setVolunteersFor] = useState(null);
  const [roster, setRoster] = useState([]);
  const [rForm, setRForm] = useState({ member_id: "", role: "Volunteer", date: new Date().toISOString().slice(0, 10) });

  const load = () => {
    api.get("/ministries").then((r) => setMinistries(r.data)).catch(() => {});
    api.get("/members").then((r) => setMembers(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openRoster = async (m) => {
    setRosterFor(m);
    setRForm({ member_id: "", role: "Volunteer", date: new Date().toISOString().slice(0, 10) });
    try { const { data } = await api.get(`/ministries/${m.id}/roster`); setRoster(data); }
    catch { setRoster([]); }
  };

  const addRoster = async () => {
    if (!rForm.member_id) { toast.error("Select a member"); return; }
    try {
      await api.post(`/ministries/${rosterFor.id}/roster`, rForm);
      const { data } = await api.get(`/ministries/${rosterFor.id}/roster`);
      setRoster(data);
      toast.success("Added to roster");
    } catch { toast.error("Could not add"); }
  };

  const removeRoster = async (id) => {
    try {
      await api.delete(`/roster/${id}`);
      setRoster((r) => r.filter((x) => x.id !== id));
    } catch { toast.error("Could not remove"); }
  };

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (m) => { setForm({ name: m.name, leader: m.leader, description: m.description, schedule: m.schedule }); setEditId(m.id); setOpen(true); };

  const save = async () => {
    if (!form.name) { toast.error("Ministry name is required"); return; }
    try {
      if (editId) { await api.put(`/ministries/${editId}`, form); toast.success("Ministry updated"); }
      else { await api.post("/ministries", form); toast.success("Ministry created"); }
      setOpen(false); load();
    } catch { toast.error("Could not save"); }
  };

  const remove = async (id) => {
    try { await api.delete(`/ministries/${id}`); toast.success("Ministry removed"); load(); }
    catch { toast.error("Could not remove"); }
  };

  return (
    <div className="space-y-6" data-testid="ministries-page">
      <div className="flex items-center justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-[#526050]">{ministries.length} active ministries</p>
          <Button variant="ghost" size="sm" data-testid="ministries-expand-all" onClick={() => saveCollapsed([])}
            className="text-[#2D5A27] hover:bg-[#EAF2E8]">Expand all</Button>
          <Button variant="ghost" size="sm" data-testid="ministries-collapse-all"
            onClick={() => saveCollapsed(groupMinistries(ministries).map((g) => g.key))}
            className="text-[#2D5A27] hover:bg-[#EAF2E8]">Collapse all</Button>
        </div>
        <Button data-testid="add-ministry-button" onClick={openNew} className="h-11 bg-[#2D5A27] hover:bg-[#23471F] text-white">
          <Plus className="h-4 w-4 mr-2" /> New Ministry
        </Button>
      </div>

      <div className="space-y-10">
        {groupMinistries(ministries).map((g, gi) => {
          const tint = TINTS[gi % TINTS.length];
          const actions = { onEdit: openEdit, onRemove: remove, onRoster: openRoster, onVolunteers: setVolunteersFor };
          return (
            <section key={g.key} className="space-y-4">
              <GroupHeader group={g} tint={tint} {...actions}
                collapsed={collapsed.includes(g.key)} onToggle={() => toggleGroup(g.key)} />
              {g.subs.length > 0 && !collapsed.includes(g.key) && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {g.subs.map((m) => <MinistryCard key={m.id} m={m} tint={tint} {...actions} />)}
                </div>
              )}
            </section>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="ministry-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editId ? "Edit Ministry" : "New Ministry"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input data-testid="ministry-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Leader</Label>
              <Input data-testid="ministry-leader-input" value={form.leader} onChange={(e) => setForm({ ...form, leader: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Schedule</Label>
              <Input value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value })} placeholder="Sun 9:00 AM" />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-ministry-button" className="bg-[#2D5A27] hover:bg-[#23471F] text-white">
              {editId ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <VolunteersDialog ministry={volunteersFor} members={members} onClose={() => setVolunteersFor(null)} />

      <Dialog open={!!rosterFor} onOpenChange={(v) => !v && setRosterFor(null)}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="roster-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{rosterFor?.name} — Roster</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
              <div className="space-y-1.5">
                <Label className="text-xs">Member</Label>
                <Select value={rForm.member_id} onValueChange={(v) => setRForm({ ...rForm, member_id: v })}>
                  <SelectTrigger data-testid="roster-member-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.first_name} {m.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Role</Label>
                <Input data-testid="roster-role-input" value={rForm.role} onChange={(e) => setRForm({ ...rForm, role: e.target.value })} placeholder="e.g. Lead Vocalist" />
              </div>
              <Button onClick={addRoster} data-testid="add-roster-button" className="bg-[#2D5A27] hover:bg-[#23471F] text-white">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Service date</Label>
              <Input type="date" value={rForm.date} onChange={(e) => setRForm({ ...rForm, date: e.target.value })} />
            </div>
            <div className="divide-y divide-[#E5E0D8] border border-[#E5E0D8] rounded-lg">
              {roster.map((r) => (
                <div key={r.id} className="flex items-center justify-between px-4 py-3" data-testid={`roster-row-${r.id}`}>
                  <div>
                    <p className="font-medium text-[#1C241B]">{r.member_name}</p>
                    <p className="text-xs text-[#7A8A78]">{r.role} · {fmtDate(r.date)}</p>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => removeRoster(r.id)} className="h-8 w-8 text-destructive" data-testid={`delete-roster-${r.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              {roster.length === 0 && <p className="px-4 py-8 text-center text-sm text-[#7A8A78]">No volunteers scheduled yet.</p>}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
